(() => {
  class AirdogCart {
    constructor() {
      this.notificationTimer = null;
      this.isUpdating = false;

      document.addEventListener(
        'submit',
        this.handleProductSubmit.bind(this),
        true
      );

      document.addEventListener(
        'click',
        this.handleClick.bind(this)
      );

      document.addEventListener(
        'change',
        this.handleChange.bind(this)
      );

      document.addEventListener(
        'keydown',
        this.handleKeydown.bind(this)
      );
    }

    get config() {
      return window.airdogCartConfig || {};
    }

    get type() {
      return this.config.type || 'page';
    }

    get cartUI() {
      return document.querySelector('[data-cart-ui]');
    }

    getJSONUrl(url) {
      if (!url) return '';

      return url.endsWith('.js') ? url : `${url}.js`;
    }

    isProductForm(form) {
      if (!form || form.dataset.airdogCartIgnore === 'true') {
        return false;
      }

      const action = form.getAttribute('action') || '';

      return action.includes('/cart/add');
    }

    async handleProductSubmit(event) {
      const form = event.target.closest('form');

      if (!this.isProductForm(form)) return;

      event.preventDefault();

      const submitButton = event.submitter;

      this.setButtonLoading(submitButton, true);
      this.clearFormError(form);

      try {
        const formData = new FormData(form);

        if (this.type !== 'page') {
          const sectionId =
            this.type === 'notification'
              ? 'airdog-cart-notification'
              : 'cart-drawer';

          formData.set('sections', sectionId);

          formData.set(
            'sections_url',
            `${window.location.pathname}${window.location.search}`
          );
        }

        const response = await fetch(
          this.getJSONUrl(this.config.addUrl),
          {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: formData
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'Unable to add this product to the cart.'
          );
        }

        if (this.type === 'page') {
          window.location.href = this.config.cartUrl;
          return;
        }

        if (this.type === 'drawer') {
          this.renderCartSection(
            data.sections?.['cart-drawer']
          );

          if (!this.cartUI?.classList.contains('is-open')) {
            this.openDrawer();
          }
        }

        await this.updateCartCount();

        if (this.type === 'notification') {
          this.showNotification();
        }

        document.dispatchEvent(
          new CustomEvent('airdog:cart-updated', {
            detail: {
              source: 'add',
              response: data
            }
          })
        );
      } catch (error) {
        console.error('Airdog cart error:', error);

        this.showFormError(
          form,
          error.message || 'Something went wrong.'
        );
      } finally {
        this.setButtonLoading(submitButton, false);
      }
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-cart-open]');

      if (openButton && this.type === 'drawer') {
        event.preventDefault();
        this.openDrawer();
        return;
      }

      const closeButton = event.target.closest('[data-cart-close]');

      if (closeButton) {
        event.preventDefault();
        this.closeCart();
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');

      if (removeButton) {
        event.preventDefault();

        this.changeLine(
          Number(removeButton.dataset.line),
          0
        );

        return;
      }

      const quantityButton = event.target.closest(
        '[data-cart-quantity]'
      );

      if (quantityButton) {
        event.preventDefault();

        const line = Number(quantityButton.dataset.line);
        const amount = Number(
          quantityButton.dataset.cartQuantity
        );

        const input = this.cartUI?.querySelector(
          `[data-cart-input][data-line="${line}"]`
        );

        if (!input) return;

        const currentQuantity = Number(input.value) || 0;
        const newQuantity = Math.max(
          0,
          currentQuantity + amount
        );

        this.changeLine(line, newQuantity);
      }
    }

    handleChange(event) {
      const input = event.target.closest('[data-cart-input]');

      if (!input) return;

      const line = Number(input.dataset.line);
      const quantity = Math.max(
        0,
        Number(input.value) || 0
      );

      this.changeLine(line, quantity);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeCart();
      }
    }

    async changeLine(line, quantity) {
      if (this.isUpdating || !line) return;

      this.isUpdating = true;
      this.setCartLoading(true);
      this.hideCartError();

      try {
        const response = await fetch(
          this.getJSONUrl(this.config.changeUrl),
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              line,
              quantity,
              sections: 'cart-drawer',
              sections_url:
                `${window.location.pathname}${window.location.search}`
            })
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            data.description ||
            data.message ||
            'Unable to update the cart.'
          );
        }

        this.renderCartSection(data.sections?.['cart-drawer']);
        this.updateCartCount();

        if (this.type === 'drawer') {
          this.openDrawer();
        }

        document.dispatchEvent(
          new CustomEvent('airdog:cart-updated', {
            detail: {
              source: 'change',
              response: data
            }
          })
        );
      } catch (error) {
        console.error('Cart update error:', error);

        this.showCartError(
          error.message || 'Unable to update the cart.'
        );
      } finally {
        this.isUpdating = false;
        this.setCartLoading(false);
      }
    }

   renderCartSection(html) {
  /*
    This renderer belongs only to the drawer section.
    Notification mode is refreshed separately.
  */
  if (this.type !== 'drawer') return;

  if (!html) {
    throw new Error('Cart content could not be refreshed.');
  }

  const currentCartUI = document.querySelector('[data-cart-ui]');

  const wasDrawerOpen =
    currentCartUI?.classList.contains('is-open');

  const wasNotificationVisible =
    currentCartUI?.classList.contains('is-visible');

  const parser = new DOMParser();

  const parsedDocument = parser.parseFromString(
    html,
    'text/html'
  );

  const currentSection = document.getElementById(
    'shopify-section-cart-drawer'
  );

  const newSection = parsedDocument.getElementById(
    'shopify-section-cart-drawer'
  );

  if (!currentSection || !newSection) {
    throw new Error('Cart drawer section was not found.');
  }

  const newCartUI = newSection.querySelector('[data-cart-ui]');
  const newDrawer = newSection.querySelector('[data-cart-drawer]');
  const newNotification = newSection.querySelector(
    '[data-cart-notification]'
  );

  const newOverlay = newSection.querySelector(
    '.airdog-cart-overlay'
  );

 
  if (wasDrawerOpen && newCartUI) {
    newCartUI.classList.add('is-open');

    if (newDrawer) {
      newDrawer.setAttribute('aria-hidden', 'false');
    }

    if (newOverlay) {
      newOverlay.hidden = false;
    }
  }

  if (wasNotificationVisible && newCartUI) {
    newCartUI.classList.add('is-visible');

    if (newNotification) {
      newNotification.setAttribute('aria-hidden', 'false');
    }
  }

  currentSection.replaceWith(newSection);

  if (wasDrawerOpen) {
    document.body.classList.add('airdog-cart-locked');
  }
}

    openDrawer() {
      const cartUI = this.cartUI;
      const drawer = cartUI?.querySelector('[data-cart-drawer]');
      const overlay = cartUI?.querySelector('.airdog-cart-overlay');

      if (!cartUI || !drawer) return;

      if (overlay) {
        overlay.hidden = false;
      }

      requestAnimationFrame(() => {
        cartUI.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.classList.add('airdog-cart-locked');
      });

      window.setTimeout(() => {
        drawer.focus();
      }, 300);
    }

    showNotification() {
      const cartUI = this.cartUI;
      const notification = cartUI?.querySelector(
        '[data-cart-notification]'
      );

      if (!cartUI || !notification) return;

      window.clearTimeout(this.notificationTimer);

      cartUI.classList.add('is-visible');
      notification.setAttribute('aria-hidden', 'false');

      this.notificationTimer = window.setTimeout(() => {
        this.closeCart();
      }, 5000);
    }

    closeCart() {
      const cartUI = this.cartUI;

      if (!cartUI) return;

      const drawer = cartUI.querySelector('[data-cart-drawer]');
      const notification = cartUI.querySelector(
        '[data-cart-notification]'
      );

      cartUI.classList.remove('is-open', 'is-visible');
      document.body.classList.remove('airdog-cart-locked');

      drawer?.setAttribute('aria-hidden', 'true');
      notification?.setAttribute('aria-hidden', 'true');

      window.clearTimeout(this.notificationTimer);

      const overlay = cartUI.querySelector(
        '.airdog-cart-overlay'
      );

      window.setTimeout(() => {
        if (overlay && !cartUI.classList.contains('is-open')) {
          overlay.hidden = true;
        }
      }, 300);
    }

    async updateCartCount() {
      try {
        const response = await fetch(
          this.getJSONUrl(
            this.config.cartUrl ||
            window.Shopify?.routes?.root + 'cart'
          ),
          {
            method: 'GET',
            headers: {
              Accept: 'application/json'
            },
            credentials: 'same-origin'
          }
        );

        if (!response.ok) {
          throw new Error(
            `Unable to refresh cart count: ${response.status}`
          );
        }

        const cart = await response.json();
        const count = Number(cart.item_count) || 0;

        document
          .querySelectorAll('[data-cart-count-source]')
          .forEach((source) => {
            source.textContent = String(count);
          });

        document
          .querySelectorAll('[data-cart-count]')
          .forEach((element) => {
            element.textContent = String(count);
            element.hidden = count === 0;
            element.setAttribute(
              'aria-label',
              `${count} ${count === 1 ? 'item' : 'items'} in cart`
            );
          });

        document.dispatchEvent(
          new CustomEvent('airdog:cart-count-updated', {
            detail: {
              count,
              cart
            }
          })
        );

        return count;
      } catch (error) {
        console.warn(
          'Airdog cart count could not be refreshed:',
          error
        );

        const source = this.cartUI?.querySelector(
          '[data-cart-count-source]'
        );

        if (!source) return 0;

        const count = Number(source.textContent.trim()) || 0;

        document
          .querySelectorAll('[data-cart-count]')
          .forEach((element) => {
            element.textContent = String(count);
            element.hidden = count === 0;
          });

        return count;
      }
    }

    setCartLoading(isLoading) {
  const cartUI = this.cartUI;

  if (!cartUI) return;

  cartUI.classList.toggle('is-loading', isLoading);
  cartUI.classList.toggle('is-updating', isLoading);
}

    setButtonLoading(button, isLoading) {
      if (!button) return;

      if (isLoading) {
        button.dataset.originalText =
          button.textContent.trim();

        button.disabled = true;
        button.textContent = 'Adding...';
      } else {
        button.disabled = false;

        if (button.dataset.originalText) {
          button.textContent =
            button.dataset.originalText;
        }
      }
    }

    showCartError(message) {
      const errorElement = this.cartUI?.querySelector(
        '[data-cart-error]'
      );

      if (!errorElement) return;

      errorElement.textContent = message;
      errorElement.hidden = false;
    }

    hideCartError() {
      const errorElement = this.cartUI?.querySelector(
        '[data-cart-error]'
      );

      if (!errorElement) return;

      errorElement.textContent = '';
      errorElement.hidden = true;
    }

    showFormError(form, message) {
      let errorElement = form.querySelector(
        '.airdog-cart-form-error'
      );

      if (!errorElement) {
        errorElement = document.createElement('div');
        errorElement.className = 'airdog-cart-form-error';
        errorElement.setAttribute('role', 'alert');

        form.appendChild(errorElement);
      }

      errorElement.textContent = message;
    }

    clearFormError(form) {
      const errorElement = form.querySelector(
        '.airdog-cart-form-error'
      );

      if (errorElement) {
        errorElement.remove();
      }
    }
  }

  new AirdogCart();


})();
(() => {
  if (window.__airdogNotificationAutoOpen) return;

  window.__airdogNotificationAutoOpen = true;

  const config = window.airdogCartConfig || {};

  if (config.type !== 'notification') return;

  const root = document.getElementById('AirdogCartUI');

  if (!root) return;

  let closeTimer;

  function openNotification() {
    window.clearTimeout(closeTimer);

    root.classList.add('is-visible');
    root.setAttribute('aria-hidden', 'false');

    const popup = root.querySelector(
      '[data-cart-notification]'
    );

    popup?.setAttribute('aria-hidden', 'false');

    closeTimer = window.setTimeout(() => {
      closeNotification();
    }, 7000);
  }

  function closeNotification() {
    window.clearTimeout(closeTimer);

    root.classList.remove('is-visible', 'is-open');
    root.setAttribute('aria-hidden', 'true');

    const popup = root.querySelector(
      '[data-cart-notification]'
    );

    popup?.setAttribute('aria-hidden', 'true');
  }

  function bindCloseButton() {
    const closeButton = root.querySelector(
      '[data-cart-close]'
    );

    if (!closeButton) return;

    closeButton.onclick = closeNotification;
  }

  async function refreshNotification() {
    try {
      const sectionUrl = new URL(
        window.Shopify?.routes?.root || '/',
        window.location.origin
      );

      sectionUrl.searchParams.set(
        'section_id',
        'airdog-cart-notification'
      );

      const response = await originalFetch(
        sectionUrl.toString(),
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Notification section request failed: ${response.status}`
        );
      }

      const html = await response.text();

      const documentFragment = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const freshNotification =
        documentFragment.querySelector(
          '[data-cart-notification]'
        );

      const currentNotification = root.querySelector(
        '[data-cart-notification]'
      );

      if (freshNotification && currentNotification) {
        currentNotification.innerHTML =
          freshNotification.innerHTML;
      }

      const cartResponse = await originalFetch(
        `${
          window.Shopify?.routes?.root || '/'
        }cart.js`,
        {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        const itemCount = Number(cart.item_count) || 0;

        document
          .querySelectorAll('[data-cart-count-source]')
          .forEach((source) => {
            source.textContent = String(itemCount);
          });

        document
          .querySelectorAll('[data-cart-count]')
          .forEach((element) => {
            element.textContent = String(itemCount);
            element.hidden = itemCount === 0;
          });
      }

      bindCloseButton();
      openNotification();
    } catch (error) {
      console.warn(
        'Airdog cart notification refresh failed:',
        error
      );

      bindCloseButton();
      openNotification();
    }
  }

  bindCloseButton();

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeNotification();
    }
  });

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      const request = args[0];
      const options = args[1] || {};

      const requestUrl =
        typeof request === 'string'
          ? request
          : request?.url || '';

      const requestMethod = (
        options.method ||
        request?.method ||
        'GET'
      ).toUpperCase();

      const requestPath = new URL(
        requestUrl,
        window.location.origin
      ).pathname;

      const isAddToCartRequest =
        requestMethod === 'POST' &&
        (
          requestPath === '/cart/add' ||
          requestPath === '/cart/add.js'
        );

      if (isAddToCartRequest && response.ok) {
        window.setTimeout(() => {
          refreshNotification();
        }, 150);
      }
    } catch (error) {
      console.warn(
        'Airdog cart notification fetch detection failed:',
        error
      );
    }

    return response;
  };

  const originalXhrOpen =
    XMLHttpRequest.prototype.open;

  const originalXhrSend =
    XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...rest
  ) {
    this.__airdogRequestMethod =
      String(method || 'GET').toUpperCase();

    this.__airdogRequestUrl = String(url || '');

    return originalXhrOpen.call(
      this,
      method,
      url,
      ...rest
    );
  };

  XMLHttpRequest.prototype.send = function (...args) {
    const requestPath = new URL(
      this.__airdogRequestUrl || '/',
      window.location.origin
    ).pathname;

    const isAddToCartRequest =
      this.__airdogRequestMethod === 'POST' &&
      (
        requestPath === '/cart/add' ||
        requestPath === '/cart/add.js'
      );

    if (isAddToCartRequest) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) {
          window.setTimeout(() => {
            refreshNotification();
          }, 150);
        }
      });
    }

    return originalXhrSend.apply(this, args);
  };
})();